/**
 * Signup funnel instrumentation.
 *
 * One call — `recordSignupEvent(stage, …)` — feeds two sinks so we can see where
 * people leak out of signup while marketing is running:
 *
 *  1. GA4 (via analytics.trackEvent) — anonymised, and GA auto-attributes the
 *     event to the visitor's source / medium / campaign. No email is ever sent.
 *  2. signup_attempts table (via the log_signup_attempt RPC) — only for genuine
 *     *missed* attempts (validation bounces, server errors, OAuth errors), so
 *     admins can see drop-off even without opening GA. Fire-and-forget.
 *
 * `started`, `email_sent` and `verified` are funnel milestones — GA only, no DB
 * row. `duplicate` (a returning user hitting signup) is GA-only too: it's not a
 * lost signup, just useful context.
 */
import { supabase } from './supabase';
import { trackEvent } from './analytics';

export type SignupStage =
  | 'started'        // landed on the signup form
  | 'duplicate'      // email already has an account → sent to sign in
  | 'validation'     // client-side validation bounced them
  | 'server_error'   // signUp() returned an error
  | 'oauth_error'    // Google sign-up failed
  | 'email_sent'     // account created, verification email dispatched
  | 'verified';      // clicked the link — funnel complete

interface SignupEventParams {
  /** Short machine-readable reason, e.g. 'password_mismatch'. */
  reason?: string;
  /** Only used for the DB log; never forwarded to GA. */
  email?: string;
  accountType?: string;
}

// Stages that represent a missed attempt worth persisting to signup_attempts.
const PERSISTED_STAGES: readonly SignupStage[] = ['validation', 'server_error', 'oauth_error'];

export function recordSignupEvent(stage: SignupStage, params: SignupEventParams = {}): void {
  const { reason, email, accountType } = params;

  // 1. GA4 — no-op when GA isn't configured; never send the raw email.
  trackEvent(`signup_${stage}`, {
    reason,
    account_type: accountType,
  });

  // 2. DB log — genuine friction only, fire-and-forget so it never blocks the UI.
  if (PERSISTED_STAGES.includes(stage)) {
    void supabase
      .rpc('log_signup_attempt', {
        p_email: email ?? null,
        p_reason: reason ?? stage,
        p_stage: stage,
        p_account_type: accountType ?? null,
        p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      })
      .then(({ error }) => {
        if (error && import.meta.env.DEV) {
          console.warn('[signupTracking] log_signup_attempt failed:', error.message);
        }
      });
  }
}
